<template>
  <div class="form-group tooth-group-service-selector"
       :class="{ readonly : readonly }"
       ref="multiselect">
    <label class="form-label">
      <span>{{ label }}</span>
    </label>
    <div class="position-relative input-part-wrapper"
         v-click-outside="clickOutside">
      <input type="text"
             ref="searchInput"
             v-model="search"
             @focus="focus"
             @click="focus"
             @input="searchChanged"
             :disabled="readonly"
             class="form-control tags-wrapper"
             :class="{ focused: dropdown, 'focused--above': dropdown && dropdownAbove }"
             :placeholder="$t('search')"/>

      <div v-if="dropdown"
           class="dropdown-search"
           :class="{ 'dropdown-search--above': dropdownAbove }">
        <div v-if="searchLoading" class="text-center p-3">
          <b-spinner label="loading"></b-spinner>
        </div>
        <div class="row no-gutters" v-if="!searchLoading">
          <div :class="{ 'col-12': !showSelectedBlock, 'col-md-7': showSelectedBlock }">
            <div class="scroll-part" v-if="!searchLoading">
              <div v-if="!servicesFiltered.length"
                   class="text-center p-2 text-muted">
                {{ $t('results_not_found') }}
              </div>
              <div v-for="(pg, pgind) in servicesFiltered"
                   :key="`price-${pgind}`"
                   :class="{ 'price-group-collapsed': !isPriceGroupOpen(pg) }">
                <div class="dropdown-price-group pointer"
                     @click="togglePriceGroup(pg)">
                  <ArrowSvg class="collapse-svg svg-gray"/>
                  {{ pg.price.title }}
                </div>
                <div class="price-group-categories">
                  <div v-for="(o, oind) in pg.categories"
                       :key="`opt-${pgind}-${oind}`">
                    <ToothGroupServiceSelectorServicesList :categoryData="o"
                                                           :teeth="teeth"
                                                           :initiallyOpened="pgind === 0 && oind === 0"
                                                           :search="search"
                                                           :readonly="readonly"
                                                           :view="view"
                                                           @select="select"
                                                           @selectedServiceId="selectedServiceId"
                                                           :commonSelectedServiceId="commonSelectedServiceId"
                                                           :servicesById="servicesById">
                      <div v-for="(s, sind) in o.subcategories"
                           :key="`opt-${pgind}-${oind}-${sind}`">
                        <ToothGroupServiceSelectorServicesList :categoryData="s"
                                                               :subcategory="true"
                                                               :initiallyOpened="pgind === 0 && oind === 0"
                                                               :search="search"
                                                               :teeth="teeth"
                                                               :readonly="readonly"
                                                               :view="view"
                                                               @select="select"
                                                               @selectedServiceId="selectedServiceId"
                                                               :commonSelectedServiceId="commonSelectedServiceId"
                                                               :servicesById="servicesById"/>
                      </div>
                    </ToothGroupServiceSelectorServicesList>
                  </div>
                </div>
              </div>
              <div v-if="search && (!options || options.length === 0)"
                   class="no-results text-muted">
                {{ $t('results_not_found') }}
              </div>
            </div>
          </div>
          <div class="col-md-5" v-if="showSelectedBlock && !isMobile">
            <div class="scroll-part">
              <div v-if="!sortedServicesQuantified.length"
                   class="text-center text-muted mt-2">
                {{ $t('services_not_selected') }}
              </div>
              <div class="d-flex flex-wrap p-2"  style="gap: 5px;">
                <div v-for="service in sortedServicesQuantified"
                     class="service-tag word-break"
                     :class="{ 'service-tag--just-added': recentlyAddedServiceIds.has(service.service.id) }"
                     :key="`service-${service.service.id}`">
                  {{ service.service.title }} <span class="text-muted">x {{ service.qty }}</span>
                  <a class="pointer font-size-0"
                     v-if="!readonly"
                     @click.stop="deleteService(service.service)">
                    <CloseSvg class="svg-black svg-size-sm"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <slot/>
  </div>
</template>

<script setup>
import {computed, getCurrentInstance, nextTick, onBeforeMount, ref, watch} from "vue"
import {useStore} from "vuex"
import {ServicesService} from "@/services/api/services.api.service"
import ToothGroupServiceSelectorServicesList
  from "@/components/parts/patients/visit/teethTab/ToothGroupServiceSelectorServicesList.vue"
import {copyObject} from "@/extensions/prototypes/prototypes"
import ArrowSvg from "@/assets/svg-vue/general/arrow_down2_tr.svg.vue"
import CloseSvg from "@/assets/svg-vue/general/close.svg"

const props = defineProps({
  readonly: Boolean,
  teeth: Array,
  label: String,
  dbSearch: {
    type: Boolean,
    default: true
  },
  allowDropdownAbove: {
    type: Boolean,
    default: false
  },
  insuranceCompanyId: {
    default: null
  },
  view: {
    type: String,
    default: 'visit'
  },
  showSelectedBlock: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
const emit = defineEmits(['select'])

let i18n = null
let noty = null

const clinic = computed(() => store.state.auth.clinic)
const superadmin = computed(() => store.state.auth.superadmin)
const branchId = computed(() => store.state.auth.branch)
const loading = computed(() => store.state.dom.loading)
const isMobile = computed(() => store.state.dom.isMobile)
const allServicesQuantified = computed(() => store.getters.recordTeethServicesWithQuantity)
const serviceIdsWithLabServicesAttached = computed(() => store.getters.serviceIdsWithLabServicesAttached)

const searchInput = ref(null)
const dropdown = ref(false)
const dropdownAbove = ref(false)
const justOpened = ref(false)
const search = ref('')
const searchLoading = ref(false)
const options = ref([])
const commonSelectedServiceId = ref(null)
const openedPriceIds = ref(new Set())
const recentlyAddedServiceIds = ref(new Set())
const highlightTimers = {}
const serviceOrder = ref([])
let loadedInsuranceCompanyId = null

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
})

const isPriceGroupOpen = (pg) => openedPriceIds.value.has(pg.price.id)

const togglePriceGroup = (pg) => {
  const newSet = new Set(openedPriceIds.value)
  if(newSet.has(pg.price.id)) newSet.delete(pg.price.id)
  else newSet.add(pg.price.id)
  openedPriceIds.value = newSet
}

const focus = async () => {
  if(props.readonly || dropdown.value) return
  launchJustOpened()
  dropdown.value = true
  await nextTick()
  const rect = searchInput.value?.getBoundingClientRect()
  if (rect && props.allowDropdownAbove) {
    const spaceBelow = window.innerHeight - rect.bottom
    dropdownAbove.value = spaceBelow < window.innerHeight * 0.45
  }
  if(!options.value.length || loadedInsuranceCompanyId !== props.insuranceCompanyId) {
    searchLoading.value = true
    serviceFind()
  }

}

const launchJustOpened = () => {
  justOpened.value = true
  setTimeout(() => {
    justOpened.value = false
  }, 100)
}

const serviceFind = () => {
  if(!props.dbSearch) return
  searchLoading.value = true
  const insuranceCompanyId = props.insuranceCompanyId
  loadedInsuranceCompanyId = insuranceCompanyId
  ServicesService.getAllServicesByPrices({
    superadmin: superadmin.value,
    clinic_id: superadmin.value ? (clinic.value?.id || null) : null,
    branch_id: branchId.value,
    insuranceCompanyId
  }).then(res => {
    if(loadedInsuranceCompanyId !== insuranceCompanyId) return
    searchLoading.value = false
    options.value = res.data
    openedPriceIds.value = new Set(res.data.length ? [res.data[0].price.id] : [])
  })
}

watch(search, (val) => {
  if(val) openedPriceIds.value = new Set(options.value.map(pg => pg.price.id))
})

watch(allServicesQuantified, (newList, oldList) => {
  if(!props.showSelectedBlock) return
  const oldIds = new Set((oldList || []).map(s => s.service.id))
  const newIds = new Set(newList.map(s => s.service.id))
  const addedIds = newList.map(s => s.service.id).filter(id => !oldIds.has(id))
  if(addedIds.length) {
    serviceOrder.value = [...addedIds, ...serviceOrder.value.filter(id => !addedIds.includes(id))]
  }
  serviceOrder.value = serviceOrder.value.filter(id => newIds.has(id))
  if(!addedIds.length) return
  const updated = new Set(recentlyAddedServiceIds.value)
  addedIds.forEach(id => {
    updated.add(id)
    clearTimeout(highlightTimers[id])
    highlightTimers[id] = setTimeout(() => {
      const s = new Set(recentlyAddedServiceIds.value)
      s.delete(id)
      recentlyAddedServiceIds.value = s
      delete highlightTimers[id]
    }, 1300)
  })
  recentlyAddedServiceIds.value = updated
})

const sortedServicesQuantified = computed(() => {
  const byId = new Map(allServicesQuantified.value.map(s => [s.service.id, s]))
  const ordered = []
  serviceOrder.value.forEach(id => {
    if(byId.has(id)) {
      ordered.push(byId.get(id))
      byId.delete(id)
    }
  })
  byId.forEach(s => ordered.push(s))
  return ordered
})

const clickOutside = () => {
  if(!dropdown.value && !search.value) return
  if(!justOpened.value) {
    dropdown.value = false
    setTimeout(() => {
      search.value = ''
    }, 100)
  }
}

const select = (service) => {
  if(loading.value) return
  emit('select', copyObject(service))
  launchJustOpened()
}

const servicesById = computed(() => {
  const data = {}
  for (const tooth of props.teeth) {
    for(const service of tooth.services) {
      if(!data[service.id]) {
        data[service.id] = {
          service,
          teeth: []
        }
      }
      data[service.id].teeth.push(tooth.teeth)
    }
  }
  return data
})

const servicesFiltered = computed(() => {
  if (!search.value) return options.value
  const q = search.value.toLowerCase()
  const matchesService = (s) => (s.name || '').toLowerCase().includes(q) || (s.cod || '').toLowerCase().includes(q)
  return options.value
      .map(priceGroup => ({
        ...priceGroup,
        categories: (priceGroup.categories || [])
            .map(category => ({
              ...category,
              services: (category.services || []).filter(matchesService),
              subcategories: (category.subcategories || [])
                  .map(sub => ({ ...sub, services: (sub.services || []).filter(matchesService) }))
                  .filter(sub => sub.services.length > 0)
            }))
            .filter(category => category.services.length > 0 || category.subcategories.length > 0)
      }))
      .filter(priceGroup => priceGroup.categories.length > 0)
})

const selectedServiceId = (val) => {
  commonSelectedServiceId.value = val
}

const searchChanged = (e) => {
  if(e?.target?.value) {
    focus()
  }
}

const deleteService = (service) => {
  if(serviceIdsWithLabServicesAttached.value.includes(service.id)) {
    noty.error(i18n.t('cannot_change_services_that_already_have_laboratory_services_attached'))
    return
  }
  store.dispatch('removeServiceFromToothGroup', {
    serviceId: service.id,
    teeth: null,
  })
}
</script>

<style scoped lang="scss">
.tags-wrapper {
  &.focused {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  &.focused--above {
    border-radius: 0 0 12px 12px;
  }
}
.dropdown-search {
  position: absolute;
  z-index: 5;
  width: 100%;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1px solid $gray-icon;
  border-top: none;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: hidden;

  &--above {
    top: auto;
    bottom: 100%;
    border-top: 1px solid $gray-icon;
    border-bottom: none;
    border-radius: 12px 12px 0 0;
  }

  .scroll-part {
    max-height: 53vh;
    overflow-y: auto;

    .dropdown-price-group {
      padding: 3px 10px;
      background: $gray-btn;
    }

    .no-results {
      padding: 10px 10px;
    }
  }
}
.collapse-svg {
  transition: 0.3s all;
}
.service-tag {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 4px 8px;
  gap: 8px;
  font-size: 90%;
  transition: all 0.5s ease;

  &--just-added {
    background: rgba($blue-color, 0.15);
  }
}
.price-group-collapsed {
  .price-group-categories {
    overflow: hidden;
    height: 0;
  }
  > .dropdown-price-group {
    .collapse-svg {
      transform: rotate(180deg);
    }
  }
}
</style>
